requestAnimationFrame(() => {
  themeBase.onDOMContentLoadedTrigger(() => {
    if (!customElements.get('slider-component')) {
      class SliderComponent extends HTMLElement {
        constructor() {
          super();
        }

        connectedCallback() {
          const connection = this.connectionVersion = (this.connectionVersion || 0) + 1;
          const requiredStyles = ['component-slider'];

          if (this.querySelector('.card')) {
            requiredStyles.push('component-card');
          }

          scriptController.onStyleReady(() => {
            if (this.isConnected && connection === this.connectionVersion) this.initializeSlider();
          }, requiredStyles);
        }

        initializeSlider() {
          if (!this.isConnected) return;
          this.sliderWaitController?.abort();
          this.sliderWaitController = new AbortController();
          if (!themeBase.isSlider(this)) {
            if (!this.activationObserver) {
              this.activationObserver = new ResizeObserver(() => {
                if (this.isConnected && themeBase.isSlider(this)) this.initializeSlider();
              });
              this.activationObserver.observe(this);
            }
            return;
          }
          this.activationObserver?.disconnect();
          this.activationObserver = null;

          this.accordionButton = this.closest('accordion-button');

          if (this.accordionButton && !this.accordionButton.classList.contains('animated-dropdown--active')) {
            this.accordionButton.addEventListener('opened', () => {
              if (this.accordionButton.classList.contains('slider-loaded')) return;
              this.initializeSlider();
              this.accordionButton.classList.add('slider-loaded');
            }, { signal: this.sliderWaitController.signal });
            return;
          }

          if (!Shopify.designMode) {
            const popupModal = this.closest('.popup-modal');

            if (popupModal && !popupModal.hasAttribute('open')) {
              popupModal.addEventListener('modalOpened', this.initializeSlider.bind(this), {
                once: true,
                signal: this.sliderWaitController.signal
              });
              return;
            }
          }

          this.slider = this.querySelector('ul.slider, [id^="Slider-"]');

          if (!this.slider) return;

          this.sliderItems = this.querySelectorAll('ul.slider li, [id^="Slide-"]');
          this.enableSliderLooping = false;
          this.currentPageElement = this.querySelector('.slider-counter--current');
          this.pageTotalElement = this.querySelector('.slider-counter--total');
          this.prevButton = this.querySelector('button[name="previous"]');
          this.nextButton = this.querySelector('button[name="next"]');
          this.bullets = this.querySelectorAll('button[name="bullet"]');
          this.initSlider();
        }

        initSlider() {
          if (!this.isConnected) return false;
          this.clearSliderRuntime();
          this.initPages();

          if (!this.sliderItemsToShow.length && !this.resizeObserver) {
            this.resizeObserver = new ResizeObserver((entries) => {
              if (this.initSlider()) {
                this.resizeObserver?.unobserve(this.slider);
              }
            });
            this.resizeObserver.observe(this.slider);
          }


          if (this.sliderItemsToShow.length < 2) {
            return false;
          }

          this.slider.style.scrollBehavior = 'auto';
          this.slider.scrollTo({
            left: 0
          });
          this.slider.style.scrollBehavior = '';

          this.sliderController = new AbortController();
          const options = { signal: this.sliderController.signal };
          this.slider.addEventListener('scroll', this.update.bind(this), options);

          if (this.prevButton) {
            this.prevButton.addEventListener('click', this.onButtonClick.bind(this), options);
          }
          if (this.nextButton) {
            this.nextButton.addEventListener('click', this.onButtonClick.bind(this), options);
          }
          if (this.dataset.autoplay !== undefined && this.sliderItemsToShow.length > 1) {
            this.startAutoplay();
          }

          this.sliderResizeObserver = new ResizeObserver(() => {
            cancelAnimationFrame(this.sliderResizeFrame);
            this.sliderResizeFrame = requestAnimationFrame(() => {
              if (this.isConnected) this.initPages();
            });
          });
          this.sliderResizeObserver.observe(this.slider);

          this.classList.add('js-slider-initialized');
          window.lazyImages.find();
          return true;
        }

        clearBulletListeners() {
          this.bulletListeners?.forEach((handler, button) => button.removeEventListener('click', handler));
          this.bulletListeners = new Map();
        }

        clearSliderRuntime() {
          this.sliderController?.abort();
          this.sliderController = null;
          this.sliderResizeObserver?.disconnect();
          this.sliderResizeObserver = null;
          this.resizeObserver?.disconnect();
          this.resizeObserver = null;
          cancelAnimationFrame(this.sliderResizeFrame);
          clearInterval(this.sliderAutoplay);
          this.sliderAutoplay = null;
        }

        disconnectedCallback() {
          this.connectionVersion = (this.connectionVersion || 0) + 1;
          this.clearSliderRuntime();
          this.sliderWaitController?.abort();
          this.sliderWaitController = null;
          this.activationObserver?.disconnect();
          this.activationObserver = null;
          this.clearBulletListeners();
        }

        initPages() {
          this.clearBulletListeners();
          if (!this.isConnected) return;
          this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);

          if (!this.slider || this.sliderItemsToShow.length < 2) return;

          this.bullets.forEach(element => element.removeAttribute('aria-current'));
          this.bulletsToShow = Array.from(this.bullets).filter((element) => element.clientWidth > 0);
          this.sliderItemsBanner = Array.from(this.sliderItems).map((element) => element.querySelector('.banner'));
          this.sliderItemsMedia = Array.from(this.sliderItems).map((element) => element.querySelector('.slideshow__media'));
          this.sliderItemsAdditionalMedia = Array.from(this.sliderItems).map((element) => element?.querySelectorAll('.slideshow__additional-media'));
          this.sliderItemsImgs = Array.from(this.sliderItemsMedia).map((element) => element?.querySelectorAll('img'));
          this.sliderItemsContent = Array.from(this.sliderItems).map((element) => element.querySelector('.banner__content'));
          this.sliderItemsBox = Array.from(this.sliderItems).map((element) => element.querySelector('.banner__box'));
          this.sliderItemsContentElements = Array.from(this.sliderItemsContent).map((element) => element?.querySelectorAll('.slideshow__content-element'));
          this.sliderLastItem = this.sliderItemsToShow[this.sliderItemsToShow.length - 1];
          this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
          this.sliderItemGap = Math.abs(this.sliderItemsToShow[0].offsetLeft + this.sliderItemsToShow[0].clientWidth - this.sliderItemsToShow[1].offsetLeft);

          this.slidesPerPage = Math.round(
            (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
          );
          this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
          this.slidePositionRatio = [];
          this.slidePositionFullPercent = [];
          this.slidePositionPercent = [];
          this.slidePositionIsActive = [];
          this.slideStep = [];

          if (this.bulletsToShow.length > 1) {
            this.bulletsToShow.forEach((element, index) => {
              const handler = this.onBulletClick.bind(this, index);
              this.bulletListeners.set(element, handler);
              element.addEventListener('click', handler);
            });
          }

          this.update();
        }

        resetPages() {
          this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
          this.initPages();
        }

        calculateSlidePosition() {
          const step = this.getStep();
          let stepGroup = 0;

          this.sliderItemsToShow.forEach((item, index) => {
            if (index === 0) {
              this.slidePositionLimit = item.clientWidth / (this.slider.clientWidth / 100) / 2;
            }

            const slideOffset = item.getBoundingClientRect().left - this.slider.getBoundingClientRect().left;

            this.slidePositionFullPercent[index] = Math.round(slideOffset / (this.slider.clientWidth / 100));
            this.slidePositionPercent[index] = this.slidePositionFullPercent[index];
            this.slidePositionRatio[index] = this.slidePositionPercent[index] / 100;
            this.slidePositionIsActive[index] = this.slidePositionPercent[index] >= this.slidePositionLimit * -1 && this.slidePositionPercent[index] <= 100 - this.slidePositionLimit;

            stepGroup = Math.floor(index / step);
            this.slideStep[index] = stepGroup;
          });

          this.slideStep[this.slideStep.length - step] = stepGroup;
        }

        getStep() {
          let step = 1;

          if (this.slider.dataset.step) {
            step = this.slider.dataset.step;

            if (themeBase.isTablet && this.slider.dataset.stepTablet) {
              step = this.slider.dataset.stepTablet;
            } else if (themeBase.isMobile && this.slider.dataset.stepMobile) {
              step = this.slider.dataset.stepMobile;
            }
          } else if (!themeBase.isDesktop && this.slider.classList.contains('slider--two-in-row')) {
            step = 2;
          }

          return new Number(step);
        }

        update() {
          if ((!this.slider || !this.nextButton) && this.dataset.autoplay === undefined) return;

          this.calculateSlidePosition();

          const previousPage = this.currentPage;

          for (let index = 0; index < this.slidePositionIsActive.length; index++) {
            if (this.slidePositionIsActive[index]) {
              this.currentPage = index;
              break;
            }
          }


          if (this.currentPageElement && this.pageTotalElement) {
            this.currentPageElement.textContent = this.currentPage + 1;
            this.pageTotalElement.textContent = this.totalPages;
          }

          if (this.currentPage != previousPage) {
            this.dispatchEvent(
              new CustomEvent('slideChanged', {
                detail: {
                  currentPage: this.currentPage,
                  currentElement: this.sliderItemsToShow[this.currentPage],
                },
              })
            );
          }

          this.bulletsToShow.forEach((element, index) => {
            const active = index === this.slideStep[this.currentPage];
            element.classList[active ? 'add' : 'remove']('active');
            if (active) element.setAttribute('aria-current', 'true');
            else element.removeAttribute('aria-current');
          });

          if (this.enableSliderLooping) return;

          if (this.prevButton) {
            if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
              this.prevButton.setAttribute('disabled', 'disabled');
            } else {
              this.prevButton.removeAttribute('disabled');
            }
          }

          if (this.nextButton) {
            if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
              this.nextButton.setAttribute('disabled', 'disabled');
            } else {
              this.nextButton.removeAttribute('disabled');
            }
          }

        }

        isSlideVisible(element, offset = 0) {
          if (!this.slider) return true;
          const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset + this.sliderItemGap;
          return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
        }

        onButtonClick(event, button) {
          if (event) {
            event.preventDefault();
          }

          const step = this.getStep();
          button = button || event.currentTarget.name;

          this.slideScrollPosition =
            button === 'next' ?
            (this.currentPage + step) * this.sliderItemOffset :
            (this.currentPage - step) * this.sliderItemOffset;

          this.setSlidePosition(this.slideScrollPosition);
        }

        setSlidePosition(position) {
          this.slider.scrollTo({
            left: position,
            behavior: 'smooth'
          });
        }

        onBulletClick(index, event) {
          if (event) event.preventDefault();

          index = index * this.getStep();

          this.slideScrollPosition = index * this.sliderItemOffset;
          this.setSlidePosition(this.slideScrollPosition);

        }

        toNextSlide() {
          let nextSlide;

          if (this.bulletsToShow.length) {

            for (let index = 0; index < this.bulletsToShow.length; index++) {
              const element = this.bulletsToShow[index];
              if (element.classList.contains('active')) {
                nextSlide = index + 1;
                if (nextSlide >= this.bulletsToShow.length) {
                  nextSlide = 0;
                }
                break;
              }
            }
          } else {
            nextSlide = this.currentPage + 1;
            if (nextSlide >= this.sliderItemsToShow.length) {
              nextSlide = 0;
            }
          }

          if (nextSlide === undefined) return;
          if (this.bulletsToShow.lenght) {
            this.bulletsToShow.forEach(element => element.classList.remove('active'));
            this.bulletsToShow[nextSlide].classList.add('active');
          }

          this.onBulletClick(nextSlide);
        }

        startAutoplay() {
          clearInterval(this.sliderAutoplay);
          this.sliderAutoplay = setInterval(() => {
            if (this.isConnected) this.toNextSlide();
          }, +this.dataset.autoplay);
        }
      }

      window.SliderComponent = SliderComponent;
      scriptController.scripts.push('slider-component.js');
      customElements.define('slider-component', SliderComponent);
    }
  });
});
